import { useEffect, useMemo, useState } from "react";
import { FieldSkeleton } from "./FieldSkeleton";
import { getSelectStyles } from "./select-styling";
import AsyncSelect, { AsyncProps } from "react-select/async";
import _ from "lodash";

type Option = {
  label: string;
  value: string | number;
};

type OwnProps = {
  innerRef?: any;
  errorMessage?: string;
  label?: string;
  helperText?: string;
  width?: string;
  onChange?: (value: any) => void;
  retrieveAsyncOptions: (inputValue: string) => Promise<Option[]>;
} & AsyncProps<any, any, any>;

export const AsyncSelectField = ({
  innerRef,
  errorMessage,
  label,
  helperText,
  value,
  width,
  retrieveAsyncOptions,
  onChange,
  ...rest
}: OwnProps) => {
  // With isMulti the form field holds an array of ids, otherwise a single id.
  const isMulti = !!rest.isMulti;
  const [value_, setValue_] = useState<Option | Option[] | null>(null);

  const debouncedLoadOptions = useMemo(() => {
    const fn = _.debounce(
      (
        inputValue: string,
        callback: (options: { label: string; value: string | number }[]) => void
      ) => {
        retrieveAsyncOptions(inputValue).then((options) => {
          callback(options);
        });
      },
      500
    );

    return fn;
  }, [retrieveAsyncOptions]); // Add retrieveAsyncOptions only if it's stable or memoized

  // The form only holds ids, so the labels have to be looked up once to show
  // the current selection. Keyed on a serialised value so a fresh array
  // reference from the form does not re-run this.
  const valueKey = Array.isArray(value) ? value.join(",") : String(value ?? "");

  useEffect(() => {
    const ids = Array.isArray(value) ? value : value ? [value] : [];
    if (ids.length === 0) {
      return;
    }
    const isHydrated = Array.isArray(value_) ? value_.length > 0 : !!value_;
    if (isHydrated) {
      return;
    }
    const setDefaultValue = async () => {
      const options = await retrieveAsyncOptions("");
      const selected = ids
        .map((id) => options.find((o) => o.value === id))
        .filter((o): o is Option => !!o);
      if (selected.length === 0) {
        return;
      }
      setValue_(isMulti ? selected : selected[0]);
    };
    setDefaultValue();
    // We don't want to have retrieveAsyncOptions in the dependency array, as it has no say in when useEffect should fire
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  useEffect(() => {
    return () => {
      debouncedLoadOptions.cancel();
    };
  }, [debouncedLoadOptions]);

  return (
    <FieldSkeleton
      errorMessage={errorMessage}
      helperText={helperText}
      label={label}
      width={width}
    >
      <AsyncSelect
        ref={innerRef}
        menuPosition="fixed"
        value={value_}
        styles={getSelectStyles({ isMulti, errorMessage })}
        onChange={(value) => {
          const newValue = value as Option | Option[] | null;
          setValue_(newValue);
          if (onChange) {
            onChange(newValue);
          }
        }}
        defaultOptions={true}
        loadOptions={(inputValue, callback) => {
          debouncedLoadOptions.cancel();
          if (inputValue === "") {
            retrieveAsyncOptions("").then(callback); // don't debounce
          } else {
            debouncedLoadOptions(inputValue, callback); // debounce for user input
          }
        }}
        {...rest}
      />
    </FieldSkeleton>
  );
};
